import { useMemo, useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { AlertTriangle, ArrowLeft, ReceiptText, Send, Pencil, GitBranch } from "lucide-react"
import {
  useGetOrderReviewQuery,
  useSubmitOrderRequestMutation,
  useResubmitOrderRequestMutation,
  usePreviewOrderApprovalQuery,
} from "shared/redux/endpoints/procurementApprovals"
import { useGetOrderLifecycleQuery } from "shared/redux/endpoints/procurementPayments"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { ORDER_STATUS, canOpenOrderEdit, canResubmitOrder, requesterActions } from "./status"
import {
  PAYMENT_METHOD_OPTIONS,
  formatVND,
  formatDate,
  formatOrderFor,
  approvalModeLabel,
  formatQuorum,
} from "./constants"
import SavedOrderBudgetAllocation from "./components/orders/SavedOrderBudgetAllocation"
import { Page, Card, Button, StatusBadge, ProcessStepper, Textarea, Alert, ApprovalRoute } from "./ui"
import { ErrorState, LinesSkeleton } from "./components/approvals/parts"
import {
  VendorProposals,
  OrderLines,
  LinkedPurchases,
  ApprovalProgress,
} from "./components/approvals/OrderCards"
import OrderSubmitConfirmModal from "./components/orders/OrderSubmitConfirmModal"
import DocumentUploader from "./components/approvals/DocumentUploader"
import DeliveryConfirmationCard from "./components/approvals/DeliveryConfirmationCard"
import PaymentLifecycleCard from "./components/approvals/PaymentLifecycleCard"
import OverBudgetBanner from "./components/orders/OverBudgetBanner"
import CloseOrderButton from "./components/orders/CloseOrderButton"
import {
  buildMissingBudgetMessage,
  getApiErrorMessage,
  resolveSubmitOutcome,
  ROUTING_FAILURE_MESSAGE,
} from "./orderSubmitFeedback"

// "Goods received" is a milestone on the linked PO (delivery_status), not a
// real OR status — the OR itself stays `approved` while that happens (see
// `stepperKey` below). It used to reuse ORDER_STATUS.INFO_REQUESTED as a
// stand-in key for that step, which collided with the real info_requested
// status: whenever an order was genuinely waiting on the requester for more
// info, the stepper matched it to this step instead and rendered Approved
// (and everything before it) as done — implying the order had already
// cleared approval when it hadn't. Use a synthetic key that can never equal
// a real ORDER_STATUS value instead.
const GOODS_RECEIVED_STEP_KEY = "goods_received"

const ORDER_STEPS = [
  { key: ORDER_STATUS.DRAFT, label: "Draft" },
  { key: ORDER_STATUS.INFO_REQUESTED, label: "Info requested" },
  { key: ORDER_STATUS.PENDING, label: "Waiting for approval" },
  { key: ORDER_STATUS.APPROVED, label: "Approved" },
  { key: GOODS_RECEIVED_STEP_KEY, label: "Goods received" },
  { key: ORDER_STATUS.CLOSED, label: "Closed" },
]

const paymentMethodLabel = (code) =>
  PAYMENT_METHOD_OPTIONS.find((o) => o.value === code)?.label || code || "—"

const Def = ({ label, children, full }) => (
  <div className={full ? "pc-def-full" : undefined}>
    <div className="pc-def-label">{label}</div>
    <div className="pc-def-value">{children || "—"}</div>
  </div>
)

// Resubmit needs a short note (what changed); Submit doesn't take a body.
// "Upload & Resubmit" doesn't ask for a file here — it implicitly grabs
// whatever the requester already staged in the Attachments card below (a
// file picked into a row whose own "Upload" button was never clicked) and
// uploads it as part of this same click, via DocumentUploader's
// `uploadPending` ref method. No need to pick the file a second time.
const ResubmitModal = ({ busy, onCancel, onSubmit, rows }) => {
  const [note, setNote] = useState("")
  return (
    <OrderSubmitConfirmModal
      title="Upload &amp; Resubmit"
      rows={rows}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onSubmit(note.trim())}
    >
      <Textarea
        label="Note"
        rows={4}
        placeholder="What did you change or add?"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={busy}
      />
      <p className="pc-hint" style={{ marginTop: 8 }}>
        Any file staged in Attachments below is uploaded automatically
        when you resubmit.
      </p>
    </OrderSubmitConfirmModal>
  )
}

// Requester's own order detail. Shared by two routes:
//   /procurement/order-requests/:id (default)
//   /procurement/my-orders/:id      (via MyOrderDetailContainer, backLabel="My Orders")
// Same OrderRequestController response shape as the manager Order Review
// screen (procurementApprovals) — reuses its cards — but the sidebar exposes
// owner actions (Edit / Submit / Resubmit) instead of Approve/Reject/Info.
const OrderDetailContainer = ({ backTo = "/procurement/order-requests", backLabel = "Order Request" }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: order, isLoading, isError, refetch } = useGetOrderReviewQuery(id, { skip: !id })
  // Order lines only carry a bare centerId — resolve to a display name for
  // the read-only lines table below (same data the create/edit form shows).
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerNameById = useMemo(() => {
    const map = {}
    ;(centersPage?.items || []).forEach((c) => {
      if (c.centerId != null) map[c.centerId] = c.centerName
    })
    return map
  }, [centersPage])
  // TODO: BE - GET /order-requests/{id}/lifecycle. `order` (with its real
  // `purchases[]`) is the arg so this mock can list payment batches per
  // linked purchase without re-fetching order data it already has.
  const { data: lifecycle } = useGetOrderLifecycleQuery(order, { skip: !order })
  // Approval route resolved from the rule engine — same preview the create/edit
  // form shows, so the requester can see who's on the route from the detail
  // page too (the live per-step state is in the Approval Progress card below).
  const { data: routePreview } = usePreviewOrderApprovalQuery(
    {
      entity: "order_request",
      category: order?.budgetCategoryId,
      region: order?.region,
      amount: order?.total,
    },
    { skip: !order?.budgetCategoryId }
  )
  const routeSteps = useMemo(
    () =>
      (routePreview?.steps || []).map((s) => ({
        ...s,
        approverLabel: Array.isArray(s.approverNames)
          ? s.approverNames.join(", ")
          : s.approverLabel || s.approvers || "—",
        modeLabel: `${approvalModeLabel(s.mode)} · ${formatQuorum(s.quorum)}`,
      })),
    [routePreview]
  )
  const [submitOrder, { isLoading: submitting }] = useSubmitOrderRequestMutation()
  const [resubmitOrder, { isLoading: resubmitting }] = useResubmitOrderRequestMutation()
  const [showResubmit, setShowResubmit] = useState(false)
  const [showSubmit, setShowSubmit] = useState(false)
  const docUploaderRef = useRef(null)

  const back = () => navigate(backTo)

  const backAction = (
    <Button variant="secondary" leftIcon={<ArrowLeft aria-hidden />} onClick={back}>
      Back to {backLabel}
    </Button>
  )

  if (isLoading) {
    return (
      <Page breadcrumb={["Procurement", backLabel, "Detail"]} title={backLabel} actions={backAction}>
        <Card>
          <LinesSkeleton lines={5} />
        </Card>
        <Card>
          <LinesSkeleton lines={4} />
        </Card>
      </Page>
    )
  }

  if (isError || !order) {
    return (
      <Page breadcrumb={["Procurement", backLabel, "Detail"]} title={backLabel} actions={backAction}>
        <Card>
          <ErrorState
            title="Couldn't load this order"
            message="The order request couldn't be loaded. It may have been removed, or the service is unavailable."
            onRetry={refetch}
          />
        </Card>
      </Page>
    )
  }

  const perms = requesterActions(order.status)
  const canEdit = canOpenOrderEdit(order)
  const showResubmitButton = canResubmitOrder(order.status)
  const busy = submitting || resubmitting
  // "Goods received" is a milestone on the PO (delivery_status), not an OR
  // status — the OR stays `approved`. Light up step 4 once any linked PO is
  // marked received so the stepper reflects reality.
  const deliveryReceived = (order.purchases || []).some((p) => p.deliveryStatus === "received")
  const stepperKey =
    order.status === ORDER_STATUS.APPROVED && deliveryReceived
      ? GOODS_RECEIVED_STEP_KEY
      : order.status
  const approvalRoutingFailed = order.status === ORDER_STATUS.PENDING && order.approvalError

  const handleSubmit = async () => {
    try {
      const submitResult = await submitOrder(id).unwrap()
      await refetch()
      const outcome = resolveSubmitOutcome({ submitResult })
      if (outcome.kind === "success") showToastSuccess(outcome.message)
      else showToastError(outcome.message)
    } catch (e) {
      // Over-budget 400 carries BE's list of missing line fields/documents — show it verbatim.
      showToastError(getApiErrorMessage(e, "Couldn't submit this order. Please try again."))
    }
  }

  const handleResubmit = async (note) => {
    if (!canResubmitOrder(order.status)) return
    try {
      // Flush anything staged in the Attachments card first — if a file
      // fails to upload, the order stays untouched instead of resubmitting
      // without the file the requester meant to attach.
      const uploadOk = await docUploaderRef.current?.uploadPending()
      if (uploadOk === false) return
      const submitResult = await resubmitOrder({ id, note }).unwrap()
      setShowResubmit(false)
      await refetch()
      const outcome = resolveSubmitOutcome({ submitResult })
      if (outcome.kind === "success") showToastSuccess("Order resubmitted for approval.")
      else showToastError(outcome.message)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't resubmit this order. Please try again."))
    }
  }

  const infoRows = [
    { label: "Order", value: order.orCode },
    { label: "Requester", value: order.requesterName },
    { label: "Category", value: order.categoryName || order.category },
    { label: "Region", value: order.region },
    { label: "Urgency", value: order.urgency || "normal" },
    { label: "Payment method", value: paymentMethodLabel(order.paymentMethod) },
    { label: "Lines", value: order.lines?.length },
    { label: "Total amount", value: formatVND(order.total) },
    { label: "Reason for request", value: order.reason, full: true },
  ]

  const headerActions = (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {backAction}
      {canEdit && (
        <Button
          variant="secondary"
          leftIcon={<Pencil aria-hidden />}
          onClick={() => navigate(`/procurement/order-requests/${id}/edit`)}
        >
          Edit
        </Button>
      )}
      {perms.canSubmit && (
        <Button
          variant="primary"
          leftIcon={<Send aria-hidden />}
          disabled={busy}
          onClick={() => setShowSubmit(true)}
        >
          Submit for Approval
        </Button>
      )}
      <CloseOrderButton order={order} onClosed={refetch} />
      {showResubmitButton && (
        <Button variant="warn" leftIcon={<Send aria-hidden />} disabled={busy} onClick={() => setShowResubmit(true)}>
          Upload &amp; Resubmit
        </Button>
      )}
    </div>
  )

  return (
    <Page
      breadcrumb={["Procurement", backLabel, order.orCode]}
      title={order.orCode}
      subtitle="Track your request through approval."
      actions={headerActions}
    >
      <OverBudgetBanner order={order} />

      {approvalRoutingFailed && (
        <Alert tone="danger" title="Approval routing needs admin attention">
          {ROUTING_FAILURE_MESSAGE}
        </Alert>
      )}

      <Card flush>
        <div style={{ padding: "18px 20px" }}>
          <ProcessStepper steps={ORDER_STEPS} currentKey={stepperKey} />
        </div>
      </Card>

      <Card
        icon={<ReceiptText aria-hidden />}
        title="Order Information"
        subtitle={order.orCode}
        action={<StatusBadge status={order.status} />}
      >
        <div className="pc-defgrid">
          <Def label="Request date">{formatDate(order.requestDate)}</Def>
          <Def label="Order for">{formatOrderFor(order.orderForType, order.orderForDates)}</Def>
          <Def label="Requester">
            {order.requesterName || "—"}
            {(order.requesterCenter || order.requesterDepartment) && (
              <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--pc-muted)", marginTop: 2 }}>
                {[order.requesterCenter, order.requesterDepartment].filter(Boolean).join(" · ")}
              </div>
            )}
          </Def>
          <Def label="Category">{order.categoryName || order.category}</Def>
          <Def label="Region">{order.region}</Def>
          <Def label="Urgency">
            <span style={{ textTransform: "capitalize" }}>{order.urgency || "normal"}</span>
          </Def>
          <Def label="Payment method">{paymentMethodLabel(order.paymentMethod)}</Def>
          <Def label="Allocation info">{order.allocationInfo}</Def>
          <Def label="Total amount">
            <span className="pc-total-lg">{formatVND(order.total)}</span>
          </Def>
          <Def label="Reason for request" full>
            <span style={{ fontWeight: 400, color: "var(--pc-body)" }}>{order.reason || "—"}</span>
          </Def>
        </div>
      </Card>

      <SavedOrderBudgetAllocation order={order} centerNameById={centerNameById} />
      <OrderLines lines={order.lines} centerNameById={centerNameById} />
      <VendorProposals proposals={order.vendorProposals} lines={order.lines} orderId={order.id || id} />
      <LinkedPurchases purchases={order.purchases} />
      <DeliveryConfirmationCard order={order} orderId={id} />
      <PaymentLifecycleCard purchases={lifecycle?.purchases} />
      {routeSteps.length > 0 && (
        <Card
          icon={<GitBranch aria-hidden />}
          title="Approval Route Preview"
          subtitle="Approvers resolved from the current rule for this category, region and amount."
        >
          {routePreview?.ruleName && (
            <p className="pc-hint" style={{ marginBottom: 12 }}>
              Matched rule: <strong>{routePreview.ruleName}</strong>
              {routePreview.version ? ` (v${routePreview.version})` : ""}
            </p>
          )}
          <ApprovalRoute steps={routeSteps} />
        </Card>
      )}
      <DocumentUploader
        ref={docUploaderRef}
        orderId={id}
        documents={order.documents}
        canUpload={order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.INFO_REQUESTED}
      />
      <ApprovalProgress steps={order.approvalProgress} />

      {showSubmit && (
        <OrderSubmitConfirmModal
          title="Submit for Approval"
          rows={infoRows}
          busy={submitting}
          onCancel={() => !submitting && setShowSubmit(false)}
          onConfirm={async () => {
            await handleSubmit()
            setShowSubmit(false)
          }}
        />
      )}

      {showResubmitButton && showResubmit && (
        <ResubmitModal
          rows={infoRows}
          busy={resubmitting}
          onCancel={() => !resubmitting && setShowResubmit(false)}
          onSubmit={handleResubmit}
        />
      )}
    </Page>
  )
}

export default OrderDetailContainer
